import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// material-ui
import Drawer from '@mui/material/Drawer';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useIntl } from 'react-intl';

// project-imports
import { AnnouncementItem, useGetAnnouncementFeed } from 'api/announcements';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';

// assets
import { Add, NotificationStatus } from 'iconsax-reactjs';

// ==============================|| HEADER CONTENT - CUSTOMIZATION ||============================== //

export default function Customization() {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const { announcementFeed, announcementFeedLoading, announcementFeedError } = useGetAnnouncementFeed();
  const handleToggle = () => {
    setOpen(!open);
  };

  const unreadCount = announcementFeed?.unreadCount ?? 0;

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) {
      return intl.formatMessage({ id: 'sentinel.common.now', defaultMessage: 'Now' });
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return intl.formatDate(parsed, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const severityColor = (item: AnnouncementItem): 'info' | 'warning' | 'error' => {
    if (item.severity === 'critical') {
      return 'error';
    }
    if (item.severity === 'warning') {
      return 'warning';
    }
    return 'info';
  };

  const renderActionButton = (item: AnnouncementItem) => {
    if (!item.ctaRoute) {
      return null;
    }

    const label = item.ctaLabel?.trim() || intl.formatMessage({ id: 'sentinel.common.open', defaultMessage: 'Open' });
    const isExternal = /^https?:\/\//i.test(item.ctaRoute);

    if (isExternal) {
      return (
        <Button component="a" href={item.ctaRoute} target="_blank" rel="noreferrer" size="small" variant="outlined">
          {label}
        </Button>
      );
    }

    return (
      <Button component={RouterLink} to={item.ctaRoute} size="small" variant="outlined" onClick={() => setOpen(false)}>
        {label}
      </Button>
    );
  };

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          color="secondary"
          variant="light"
          onClick={handleToggle}
          aria-label="settings toggler"
          size="large"
          sx={(theme) => ({
            p: 1,
            color: 'secondary.main',
            bgcolor: open ? 'secondary.200' : 'secondary.100',
            ...theme.applyStyles('dark', { bgcolor: open ? 'background.paper' : 'background.default' })
          })}
        >
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <NotificationStatus variant="Bulk" />
            {unreadCount > 0 ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -6,
                  minWidth: 18,
                  height: 18,
                  px: 0.5,
                  borderRadius: 10,
                  bgcolor: 'error.main',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Box>
            ) : null}
          </Box>
        </IconButton>
      </Box>
      <Drawer
        sx={{ zIndex: 2001 }}
        anchor="right"
        onClose={handleToggle}
        open={open}
        slotProps={{ paper: { sx: { width: { xs: 350, sm: 474 } } } }}
      >
        {open && (
          <MainCard content={false} sx={{ border: 'none', borderRadius: 0, height: '100vh' }}>
            <SimpleBar sx={{ '& .simplebar-content': { display: 'flex', flexDirection: 'column' } }}>
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h5">{intl.formatMessage({ id: 'sentinel.announcements.title', defaultMessage: 'Announcements' })}</Typography>
                  <IconButton color="error" sx={{ p: 0 }} onClick={handleToggle}>
                    <Add size={28} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {intl.formatMessage({
                    id: 'sentinel.announcements.drawer_description',
                    defaultMessage: 'Curated system bulletins for operators: maintenance windows, release notes, and high-signal incident updates.'
                  })}
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {announcementFeedLoading ? (
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary">
                        {intl.formatMessage({ id: 'sentinel.announcements.loading', defaultMessage: 'Loading announcements...' })}
                      </Typography>
                    </Stack>
                  ) : null}

                  {!announcementFeedLoading && announcementFeedError ? (
                    <Alert severity="error">
                      {intl.formatMessage({
                        id: 'sentinel.announcements.load_error',
                        defaultMessage: 'Unable to load announcements from the Control API right now.'
                      })}
                    </Alert>
                  ) : null}

                  {!announcementFeedLoading && !announcementFeedError && !(announcementFeed?.items?.length ?? 0) ? (
                    <Alert severity="info">
                      {intl.formatMessage({ id: 'sentinel.announcements.empty', defaultMessage: 'No active announcements right now.' })}
                    </Alert>
                  ) : null}

                  {!announcementFeedLoading && !announcementFeedError
                    ? (announcementFeed?.items ?? []).map((item) => (
                        <MainCard
                          key={item.id}
                          contentSX={{ p: 2 }}
                          sx={{
                            borderColor: item.severity === 'critical' ? 'error.light' : item.severity === 'warning' ? 'warning.light' : 'divider'
                          }}
                        >
                          <Stack spacing={1.25}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                              <Box>
                                <Typography variant="h5">{item.title}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(item.startsAt ?? item.publishedAt ?? item.updatedAt)}
                                </Typography>
                              </Box>
                              <Chip
                                label={
                                  item.severity === 'critical'
                                    ? intl.formatMessage({ id: 'sentinel.common.severity.critical', defaultMessage: 'Critical' })
                                    : item.severity === 'warning'
                                      ? intl.formatMessage({ id: 'sentinel.common.severity.warning', defaultMessage: 'Warning' })
                                      : intl.formatMessage({ id: 'sentinel.common.severity.info', defaultMessage: 'Info' })
                                }
                                color={severityColor(item)}
                                size="small"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {item.message}
                            </Typography>
                            {renderActionButton(item)}
                          </Stack>
                        </MainCard>
                      ))
                    : null}
                </Stack>
                <Divider sx={{ my: 2.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button component={RouterLink} to="/announcements" onClick={() => setOpen(false)}>
                    {intl.formatMessage({ id: 'sentinel.announcements.manage', defaultMessage: 'Manage announcements' })}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {announcementFeed?.generatedAt
                      ? intl.formatMessage(
                          { id: 'sentinel.common.updated_at', defaultMessage: 'Updated {value}' },
                          { value: formatDateTime(announcementFeed.generatedAt) }
                        )
                      : ''}
                  </Typography>
                </Stack>
              </Box>
            </SimpleBar>
          </MainCard>
        )}
      </Drawer>
    </>
  );
}
